import { ProductBase as Entity } from './product-base.model';
import { AppState } from '../app-store.state';
import { Injectable } from '@angular/core';
import {select, Store} from '@ngrx/store';
import {Observable, Subject} from 'rxjs';
import {IPage, ITableSort} from "../app-store.interfaces";
import {isCurrentEntity, ProductBaseSelectors as Selectors} from "./product-base.selectors";
import {ProductBaseActions as Actions} from './product-base.actions';
import {Criteria} from "../app-store.types";
import {generateUniqueId} from "../app-store.functions";
import {PaginatedResult} from "../app-store-entity.service";
import {filter} from "rxjs/operators";
import {UpdateProductBaseRequest} from "./update-product-base-request.model";

@Injectable({ providedIn: 'root' })
export class ProductBaseFacade {
  all$: Observable<Entity[]> = this.store.pipe(select(Selectors.selectAll));
  isLoading$: Observable<boolean> = this.store.pipe(select(Selectors.isLoading));
  hasError$: Observable<Error | undefined> = this.store.pipe(select(Selectors.hasErrors));
  current$: Observable<Entity | undefined> = this.store.pipe(select(Selectors.currentEntity));
  currentId$: Observable<number | undefined> = this.store.pipe(select(Selectors.currentEntityId));
  currentEntities$: Observable<Entity[] | undefined> = this.store.pipe(select(Selectors.currentEntities));
  currentIds$: Observable<number[] | undefined> = this.store.pipe(select(Selectors.currentEntityIds));
  totalPageable$: Observable<number> = this.store.pipe(select(Selectors.totalPageable));
  currentPage$: Observable<IPage> = this.store.pipe(select(Selectors.currentPage));
  currentPageEntities$: Observable<Entity[] | undefined> = this.store.pipe(select(Selectors.currentPageEntities));
  currentSort$: Observable<ITableSort | undefined> = this.store.pipe(select(Selectors.currentSort));

  isLoadingCurrentList$: Observable<boolean> = this.store.pipe(select(Selectors.isLoadingCurrentList));
  currentListEntities$: Observable<Entity[] | undefined> = this.store.pipe(select(Selectors.currentListEntities));
  currentEditorEntities$: Observable<Entity[] | undefined> = this.store.pipe(select(Selectors.currentEditorPageEntities));
  currentListEntitiesCurrentPage$: Observable<IPage> = this.store.pipe(select(Selectors.currentListEntitiesCurrentPage));

  isLoadingEditor$: Observable<boolean> = this.store.pipe(select(Selectors.isLoading));
  isSaving$: Observable<boolean> = this.store.pipe(select(Selectors.isSaving));
  currentEditorEntitiesCurrentPage$: Observable<IPage> = this.store.pipe(select(Selectors.currentEditorEntitiesCurrentPage));
  currentEditorEntitiesCurrentSort$: Observable<ITableSort | undefined> = this.store.pipe(select(Selectors.currentEditorEntitiesCurrentSort));
  currentEditorEntitiesTotalPageable$: Observable<number> = this.store.pipe(select(Selectors.currentEditorEntitiesTotalPageable));

  private _refreshPage = new Subject<void>();
  refreshPage$: Observable<void> = this._refreshPage.asObservable();

  private _refreshPageEditor = new Subject<void>();
  refreshPageEditor$: Observable<void> = this._refreshPageEditor.asObservable();

  private _refreshPageList = new Subject<void>();
  refreshPageList$: Observable<void> = this._refreshPageList.asObservable();

  constructor(
    private store: Store<AppState>,
  ) {

  }

  loadAndSelectIfNecessary(productBaseId: number, productCategoryId: number | undefined = undefined): void {
    this.store.dispatch(Actions.loadAndSelectIfNecessary({productBaseId, productCategoryId}));
  }

  refreshPage(): void {
    this._refreshPage.next();
  }

  refreshPageForEditor(): void {
    this._refreshPageEditor.next();
  }

  clearPage(): void {
    this.store.dispatch(Actions.clearPage());
  }

  isCurrentEntity(entityId: number): Observable<boolean> {
    return this.store.select(isCurrentEntity(entityId));
  }

  setPaginationSettings(page: IPage): void {
    this.store.dispatch(Actions.setPaginationSettings({page}));
    this.refreshPage();
  }

  setPaginationSettingsForEditor(page: IPage): void {
    this.store.dispatch(Actions.setPaginationSettingsForEditor({page}));
    this.refreshPageForEditor();
  }

  setSortSettings(tableSort: ITableSort): void {
    this.store.dispatch(Actions.setSortSettings({tableSort}));
  }

  setCurrentId(entityId: number): void {
    this.store.dispatch(Actions.setCurrentEntityId({entityId}));
  }

  selectEntity(entityId: number): void {
    this.store.dispatch(Actions.selectEntity({entityId}));
  }

  deselectEntity(entityId: number): void {
    this.store.dispatch(Actions.deselectEntity({entityId}));
  }

  deselectAll(): void {
    this.store.dispatch(Actions.deselectEntities());
  }

  loadPage(pageNumber: number, pageSize: number, criteria: Criteria = undefined): void {
      this.store.dispatch(Actions.loadPage({criteria, page: {pageNumber, pageSize}}));
  }

  loadPageWithResult(pageNumber: number, pageSize: number, criteria: Criteria = undefined): Observable<PaginatedResult<Entity>> {
    const correlationId = generateUniqueId();
    this.store.dispatch(Actions.loadPage({page: {pageNumber, pageSize}, criteria, correlationId: correlationId}));
    return this.store.select(Selectors.findByCorrelationId<PaginatedResult<Entity>>(correlationId))
      .pipe(filter((x) => !!x));
  }

  loadProductBasesPageForEditorWithResult(pageNumber: number, pageSize: number, criteria: Criteria = undefined): Observable<PaginatedResult<Entity>>{
      const correlationId = generateUniqueId();
    this.store.dispatch(Actions.loadProductBasePageForEditor({page: {pageNumber, pageSize}, criteria}));
      return this.store.select(Selectors.findByCorrelationId<PaginatedResult<Entity>>(correlationId))
          .pipe(filter((x) => !!x));
  }

  loadListPage(pageNumber: number, pageSize: number, criteria: Criteria = undefined): void {
    this.store.dispatch(Actions.loadListPage({criteria, page: {pageNumber, pageSize}}));
  }

  load(criteria: Criteria = undefined): void {
    this.store.dispatch(Actions.load({criteria}));
  }

  loadWithResult(criteria: Criteria = undefined): Observable<Entity[]> {
    const correlationId = generateUniqueId();
    this.store.dispatch(Actions.load({criteria, correlationId: correlationId}));

    return this.store.select(Selectors.findByCorrelationId<Entity[]>(correlationId))
      .pipe(filter((x) => !!x));
  }

  // loadSingleWithResult(entityId: number): Observable<Entity> {
  //   const correlationId = generateUniqueId();
  //   this.store.dispatch(Actions.loadSingleIfNecessary({entityId, correlationId: correlationId}));
  //
  //   return this.store.select(Selectors.findByCorrelationId<Entity>(correlationId))
  //     .pipe(filter((x) => !!x), first());
  // }


  create(entity: Entity): void {
    this.store.dispatch(Actions.create({entity}));
  }

  createWithResult(entity: Entity): Observable<Entity> {
    const correlationId = generateUniqueId();

    this.store.dispatch(Actions.create({entity, correlationId: correlationId}));

    return this.store.select(Selectors.findByCorrelationId<Entity>(correlationId))
      .pipe(filter((x) => !!x));
  }

  update(entity: Entity): void {
    this.store.dispatch(Actions.update({entity, correlationId: generateUniqueId()}));
  }

  updateWithResult(entity: Entity): Observable<Entity> {
    const correlationId = generateUniqueId();

    this.store.dispatch(Actions.update({entity, correlationId: correlationId}));

    return this.store.select(Selectors.findByCorrelationId<Entity>(correlationId))
      .pipe(filter((x) => !!x));
  }

  updateRequestWithResult(entity: UpdateProductBaseRequest): Observable<Entity> {
    const correlationId = generateUniqueId();

    this.store.dispatch(Actions.updateRequest({entity, correlationId: correlationId}));

    return this.store.select(Selectors.findByCorrelationId<Entity>(correlationId))
      .pipe(filter((x) => !!x));
  }

  remove(entityId: number): void {
    this.store.dispatch(Actions.remove({entityId}));
  }

  removeWithResult(entityId: number): Observable<Entity> {
    const correlationId = generateUniqueId();

    this.store.dispatch(Actions.remove({entityId, correlationId: correlationId}));

    return this.store.select(Selectors.findByCorrelationId<Entity>(correlationId))
      .pipe(filter((x) => !!x));
  }

  clone(entity: Entity): void {
    this.store.dispatch(Actions.clone({entity}));
  }

  cloneWithResult(entity: Entity): Observable<Entity> {
    const correlationId = generateUniqueId();

    this.store.dispatch(Actions.clone({entity, correlationId: correlationId}));

    return this.store.select(Selectors.findByCorrelationId<Entity>(correlationId))
      .pipe(filter((x) => !!x));
  }

  getById(entityId: number): Observable<Entity | undefined> {
    return this.store.pipe(select(Selectors.selectById(entityId)));
  }

  getTotalCount(): Observable<number> {
    return this.store.select(Selectors.totalCount);
  }

  setTotalCount(): void {
    this.store.dispatch(Actions.getTotalProductCount());
  }
}
